import {
  Directive,
  DoCheck,
  ElementRef,
  OnDestroy,
  OnInit,
  Renderer2,
  forwardRef,
  inject,
  input
} from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';

/** Segundos que permanece visible el aviso de recorte por pegado. */
const AVISO_RECORTE_MS = 6000;
/** Porcentaje del límite desde el cual el contador pasa a ámbar. */
const UMBRAL_CERCA_LIMITE = 0.9;

let secuencia = 0;

/**
 * DIRECTIVA: appContadorCaracteres
 *
 * Contador "actual / máximo" bajo un `<input>` o `<textarea>` con límite de
 * caracteres. Es solo UX: la validación definitiva sigue en el backend.
 *
 * - Colores: gris normal, ámbar >= 90 %, rojo en el límite o excedido.
 * - Pegado: si el texto pegado se recorta (maxlength nativo) avisa cuántos
 *   caracteres se perdieron; el aviso se oculta solo tras unos segundos.
 * - `permitirExceder`: no aplica maxlength; el campo queda inválido
 *   (aria-invalid, borde rojo y error `maxlength` en el control del formulario)
 *   hasta que el texto vuelva a estar dentro del límite.
 * - Accesibilidad: mensaje con aria-live="polite"; contador y mensaje enlazados
 *   al campo con aria-describedby.
 *
 * El límite debe venir de `constants-ejb.ts` (misma fuente que el backend).
 * Aplica a `formControlName`, `[formControl]` y `ngModel` sin cambios.
 *
 * @example
 * <textarea pInputTextarea formControlName="obs"
 *           [appContadorCaracteres]="MAX_RECOMENDACIONES_CUMPLIMIENTO"></textarea>
 */
@Directive({
  selector: 'input[appContadorCaracteres], textarea[appContadorCaracteres]',
  standalone: true,
  host: {
    '(input)': 'render()',
    '(paste)': 'onPaste($event)'
  },
  providers: [
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => ContadorCaracteresDirective), multi: true }
  ]
})
export class ContadorCaracteresDirective implements OnInit, DoCheck, OnDestroy, Validator {
  private readonly el = inject<ElementRef<HTMLInputElement | HTMLTextAreaElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);

  /** Límite máximo de caracteres. */
  readonly appContadorCaracteres = input.required<number>();
  /** true: se puede exceder el límite al pegar, pero el campo queda inválido. */
  readonly permitirExceder = input<boolean>(false);

  private readonly idBase = `contador-caracteres-${++secuencia}`;
  private contenedor!: HTMLElement;
  private conteoEl!: HTMLElement;
  private mensajeEl!: HTMLElement;
  private avisoRecorte = '';
  private temporizador: ReturnType<typeof setTimeout> | null = null;
  private ultimaFirma = '';

  ngOnInit(): void {
    const host = this.el.nativeElement;
    const mensajeId = `${this.idBase}-msg`;
    const conteoId = `${this.idBase}-count`;

    this.contenedor = this.renderer.createElement('div');
    this.renderer.addClass(this.contenedor, 'sura-char-counter');
    this.mensajeEl = this.renderer.createElement('span');
    this.renderer.addClass(this.mensajeEl, 'sura-char-counter__msg');
    this.renderer.setAttribute(this.mensajeEl, 'id', mensajeId);
    this.renderer.setAttribute(this.mensajeEl, 'aria-live', 'polite');
    this.conteoEl = this.renderer.createElement('span');
    this.renderer.addClass(this.conteoEl, 'sura-char-counter__count');
    this.renderer.setAttribute(this.conteoEl, 'id', conteoId);
    this.renderer.appendChild(this.contenedor, this.mensajeEl);
    this.renderer.appendChild(this.contenedor, this.conteoEl);

    const padre = this.renderer.parentNode(host);
    this.renderer.insertBefore(padre, this.contenedor, this.renderer.nextSibling(host));

    const previos = host.getAttribute('aria-describedby');
    this.renderer.setAttribute(
      host,
      'aria-describedby',
      [previos, conteoId, mensajeId].filter(Boolean).join(' ')
    );
    this.render();
  }

  /** Cubre cambios programáticos (patchValue, ngModel, carga inicial). */
  ngDoCheck(): void {
    if (this.contenedor) this.render();
  }

  ngOnDestroy(): void {
    if (this.temporizador) clearTimeout(this.temporizador);
    this.contenedor?.remove();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const valor = control.value;
    const max = this.appContadorCaracteres();
    if (typeof valor !== 'string' || !max || valor.length <= max) return null;
    return { maxlength: { requiredLength: max, actualLength: valor.length } };
  }

  onPaste(event: ClipboardEvent): void {
    if (this.permitirExceder()) return; // el exceso se informa en render()
    const max = this.appContadorCaracteres();
    const host = this.el.nativeElement;
    // El valor del campo cuenta cada salto de línea como 1 carácter
    const pegado = (event.clipboardData?.getData('text') ?? '').replace(/\r\n/g, '\n');
    const seleccionados = (host.selectionEnd ?? 0) - (host.selectionStart ?? 0);
    const perdidos = host.value.length - seleccionados + pegado.length - max;
    if (perdidos <= 0) return;

    this.avisoRecorte =
      `El texto pegado superaba el máximo de ${max} caracteres. ` +
      (perdidos === 1 ? 'Se recortó 1 carácter.' : `Se recortaron ${perdidos} caracteres.`);
    if (this.temporizador) clearTimeout(this.temporizador);
    this.temporizador = setTimeout(() => {
      this.avisoRecorte = '';
      this.temporizador = null;
      this.render();
    }, AVISO_RECORTE_MS);
    this.render();
  }

  render(): void {
    const host = this.el.nativeElement;
    const max = this.appContadorCaracteres();
    const actual = host.value.length;
    const exceso = Math.max(0, actual - max);
    const excede = exceso > 0;
    const enLimite = actual >= max;
    const cercaDelLimite = actual >= max * UMBRAL_CERCA_LIMITE;
    const mensaje = excede
      ? `Has superado el máximo de ${max} caracteres por ${exceso}.`
      : this.avisoRecorte;

    const firma = `${actual}|${max}|${this.permitirExceder()}|${mensaje}`;
    if (firma === this.ultimaFirma) return;
    this.ultimaFirma = firma;

    if (this.permitirExceder()) this.renderer.removeAttribute(host, 'maxlength');
    else this.renderer.setAttribute(host, 'maxlength', String(max));

    this.conteoEl.textContent = `${actual} / ${max}`;
    this.mensajeEl.textContent = mensaje;
    this.setClase(this.conteoEl, 'sura-char-counter__count--warn', cercaDelLimite && !enLimite);
    this.setClase(this.conteoEl, 'sura-char-counter__count--limit', enLimite);
    this.setClase(this.mensajeEl, 'sura-char-counter__msg--error', excede);
    this.setClase(host, 'sura-char-counter--excedido', excede);
    if (excede) this.renderer.setAttribute(host, 'aria-invalid', 'true');
    else this.renderer.removeAttribute(host, 'aria-invalid');
  }

  private setClase(elemento: HTMLElement, clase: string, activa: boolean): void {
    if (activa) this.renderer.addClass(elemento, clase);
    else this.renderer.removeClass(elemento, clase);
  }
}
